import { useMemo, useState } from "react";

export type StakeholderOption = { readonly id: string; readonly name: string };

// Stands in for a stakeholder id on the create-on-the-fly row, which has none.
const CREATE_KEY = "__create__";

// Type-to-search over existing stakeholders with a create-on-the-fly option.
// Selecting an existing one calls onPick({ stakeholderId }); creating calls
// onPick({ name }). Used by the transactions bulk-attribute popover and the
// rules page editor.
export default function StakeholderCombobox({
  options,
  onPick,
  autoFocus = true,
  placeholder = "Type a counterparty…",
  busy = false,
}: {
  options: ReadonlyArray<StakeholderOption>;
  onPick: (choice: { stakeholderId?: string; name?: string }) => void;
  autoFocus?: boolean;
  placeholder?: string;
  // The caller's pick is still in flight: lock the list and spin on the row that
  // was clicked, so a slow bulk attribution doesn't read as a dead click.
  busy?: boolean;
}) {
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  // Only read while `busy`, so a failed pick needs no reset — the next pick
  // overwrites it.
  const [picked, setPicked] = useState<string | null>(null);
  const pick = (
    key: string,
    choice: { stakeholderId?: string; name?: string }
  ) => {
    setPicked(key);
    onPick(choice);
  };
  const spinner = (
    <span className="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
  );

  const matches = useMemo(() => {
    if (!trimmed) return options.slice(0, 8);
    const needle = trimmed.toLowerCase();
    return options
      .filter((o) => o.name.toLowerCase().includes(needle))
      .slice(0, 8);
  }, [options, trimmed]);

  const exactExists = options.some(
    (o) => o.name.toLowerCase() === trimmed.toLowerCase()
  );

  return (
    <div className="w-72 text-sm">
      <input
        autoFocus={autoFocus}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        disabled={busy}
        className="w-full rounded border px-2 py-1.5 text-sm disabled:bg-gray-50"
      />
      <div className="mt-1 max-h-64 overflow-y-auto">
        {matches.map((option) => (
          <button
            key={option.id}
            type="button"
            disabled={busy}
            onClick={() => pick(option.id, { stakeholderId: option.id })}
            className="flex w-full items-center justify-between gap-2 text-left px-2 py-1.5 hover:bg-gray-50 rounded disabled:opacity-50"
          >
            <span className="truncate">{option.name}</span>
            {busy && picked === option.id && spinner}
          </button>
        ))}
        {trimmed && !exactExists && (
          <button
            type="button"
            disabled={busy}
            onClick={() => pick(CREATE_KEY, { name: trimmed })}
            className="flex w-full items-center justify-between gap-2 text-left px-2 py-1.5 font-medium text-emerald-700 hover:bg-emerald-50 rounded border-t disabled:opacity-50"
          >
            <span className="truncate">{`+ create "${trimmed}"`}</span>
            {busy && picked === CREATE_KEY && spinner}
          </button>
        )}
        {matches.length === 0 && !trimmed && (
          <p className="px-2 py-1.5 text-gray-400">Start typing to search…</p>
        )}
      </div>
    </div>
  );
}
